import { useQuery } from '@apollo/client/react'
import { useEffect, useRef } from 'react'
import { DOWNVOTE_STATUS } from '@/fragments/monero'
import { shouldTriggerPaymentSuccess } from '@/lib/pay-in'

// Polls downvoteStatus(paymentId) while a downvote modal is open. Fires
// onDetected exactly once when the rewardsWalletObserver first observes the payment
// (DETECTED) OR when the downvote is already CONFIRMED, then stops. CONFIRMED,
// normally reached via confirmFinalizer, must ALSO trigger success — a downvote
// already confirmed by the time the modal first polls would otherwise be a silent
// stop-polling terminal leaving the modal stuck with no success message and no
// close. REORGED/EXPIRED are failure terminals. Mirrors the tip poll
// (components/tip/use-watch-tip.js).
//
// Polling is a plain setInterval + refetch: Apollo's own pollInterval cannot be
// relied on to survive React 19 StrictMode's double-mount (the remount reuses
// the cached ObservableQuery and never restarts the timer), while a fresh
// interval per mount always runs. The interval is torn down on unmount and on
// terminal states, so no timer outlives the modal.
const POLL_INTERVAL_MS = 3000

// Terminal states: stop polling once the downvote reaches any of these.
// DETECTED/CONFIRMED are success triggers (fire onDetected); REORGED/EXPIRED are
// failure terminals. null/PENDING keep polling.
const TERMINAL_STATES = new Set(['DETECTED', 'CONFIRMED', 'REORGED', 'EXPIRED'])

export default function useWatchDownvote ({ paymentId, onDetected }) {
  const { data, refetch } = useQuery(DOWNVOTE_STATUS, {
    variables: { paymentId },
    skip: !paymentId
  })

  const state = data?.downvoteStatus?.state ?? null
  const firedRef = useRef(false)

  useEffect(() => {
    if (TERMINAL_STATES.has(state)) return
    const timer = setInterval(() => {
      refetch().catch(() => {})
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [state, refetch])

  useEffect(() => {
    if (shouldTriggerPaymentSuccess(state) && !firedRef.current) {
      firedRef.current = true
      onDetected?.()
    }
  }, [state, onDetected])

  return { state }
}
